import * as React from 'react';
import { MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ooh/ui/components/dropdown-menu';
import {
  ROW_LINK_ANCHOR_CLASSNAME,
  INTERACTIVE_CELL_CLASSNAME,
} from '@ooh/ui/components/data-table';
import { TableCell, TableRow } from '@ooh/ui/components/table';
import { cn } from '@ooh/ui/lib/utils';
import { formatDisplayDate } from '@ooh/ui/lib/date-format';
import { Link } from '../../plugs/link';
import { ExpiryDeadlineCell } from './expiry-deadline-cell';
import {
  buildRowActions,
  HolderCell,
  OfferCell,
  STICKY_COL_SHADOW,
  WaitingCountCell,
} from './reservation-list-cells';
import { ReservationQueueList } from './reservation-queue-list';
import type {
  QueueAction,
  QueueEntryView,
  ReservationQueueListLabels,
} from './reservation-queue-list';
import { ReservationStatusBadge } from './reservation-status-badge';
import type {
  ReservationListItem,
  ReservationListViewLabels,
  ReservationListViewPermissions,
} from './reservation-list-view-types';

export interface ReservationListRowProps {
  item: ReservationListItem;
  index: number;
  permissions: ReservationListViewPermissions;
  labels: ReservationListViewLabels;
  currentUserId: number;
  queueEntries: QueueEntryView[];
  queueLabels?: ReservationQueueListLabels;
  isQueueOpen: boolean;
  onToggleQueue: (reservationId: number) => void;
  onAction: (action: string, reservationId: number) => void;
  onBlockAvailability?: (mediaUnitId: number) => void;
  onQueueAction?: (action: QueueAction, entryId: number, reservationId: number) => void;
}

// eslint-disable-next-line complexity -- row renderer with optional queue affordance; extraction would split a cohesive row (same precedent as UnitReservationsTab).
export function ReservationListRow({
  item,
  index,
  permissions,
  labels,
  currentUserId,
  queueEntries,
  queueLabels,
  isQueueOpen,
  onToggleQueue,
  onAction,
  onBlockAvailability,
  onQueueAction,
}: ReservationListRowProps) {
  const rowActions = buildRowActions(item, permissions, currentUserId, labels);
  const isQueued = item.isQueued;

  const rowBg = index % 2 === 0 ? 'bg-background' : 'bg-muted/10';
  const hasQueue = queueEntries.length > 0 && queueLabels !== undefined;
  return (
    <>
      <TableRow
        data-testid={`reservation-row-${item.id}`}
        className={cn(
          'relative cursor-pointer transition-colors border-b border-border/60 hover:bg-muted/30',
          rowBg,
        )}
      >
        {/* Status */}
        <TableCell className="px-4 py-2.5">
          <ReservationStatusBadge
            status={item.status}
            isQueued={isQueued}
            statusLabel={item.statusLabel}
          />
        </TableCell>
        {/* Media unit */}
        <TableCell className="px-4 py-2.5">
          <Link
            to={item.unitHref}
            className={cn(ROW_LINK_ANCHOR_CLASSNAME, 'flex-col')}
            data-testid={`reservation-row-unit-link-${item.id}`}
          >
            <span className="block text-[13px] font-medium text-foreground truncate max-w-[190px]">
              {item.mediaUnitName}
            </span>
            <span className="block text-2xs text-muted-foreground">
              {item.mediaUnitCity} · {item.mediaUnitCode}
            </span>
          </Link>
        </TableCell>
        {/* Date from */}
        <TableCell className="px-4 py-2.5">
          <span className="text-[13px]">{formatDisplayDate(item.dateFrom)}</span>
        </TableCell>
        {/* Date to */}
        <TableCell className="px-4 py-2.5">
          <span className="text-[13px]">{formatDisplayDate(item.dateTo)}</span>
        </TableCell>
        {/* Expiry deadline — see header comment above. */}
        <TableCell className="w-32 min-w-28 px-4 py-2.5">
          <ExpiryDeadlineCell
            status={item.status}
            effectiveExpiresAt={item.effectiveExpiresAt ?? null}
            labels={{
              neverExpiresLabel: labels.deadlineNeverExpiresLabel ?? '',
              noDeadlineSetLabel: labels.deadlineNoDeadlineSetLabel ?? '',
              expiredMarkerLabel: labels.deadlineExpiredMarkerLabel ?? '',
            }}
            testId={`reservation-deadline-${String(item.id)}`}
          />
        </TableCell>
        {/* Queue — number of people waiting behind this reservation.
            Column order must match the header above. */}
        <TableCell
          className={cn(
            'w-[86px] min-w-[86px] whitespace-nowrap px-4 py-2.5 text-center',
            INTERACTIVE_CELL_CLASSNAME,
          )}
        >
          {item.waitingCount && item.waitingCount > 0 ? (
            <WaitingCountCell
              item={item}
              label={labels.queuedTooltipLabel ?? labels.queuedLabel}
              isOpen={isQueueOpen}
              onToggle={hasQueue ? () => onToggleQueue(item.id) : undefined}
            />
          ) : null}
        </TableCell>
        <TableCell className="px-4 py-2.5">
          <HolderCell item={item} labels={labels} />
        </TableCell>
        <TableCell className={cn('px-4 py-2.5', INTERACTIVE_CELL_CLASSNAME)}>
          <OfferCell item={item} labels={labels} />
        </TableCell>
        {/* Source */}
        <TableCell className="px-4 py-2.5">
          <span className="text-2xs text-muted-foreground">{item.sourceLabel}</span>
        </TableCell>
        {/* Sticky actions — solid bg-background always, no alternation to prevent bleed */}
        <TableCell
          className={cn(
            'sticky right-0 z-10 px-2 py-2.5 text-center bg-background',
            STICKY_COL_SHADOW,
          )}
        >
          {rowActions.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid={`reservation-row-actions-trigger-${item.id}`}
                  className="flex items-center justify-center h-7 w-7 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors mx-auto"
                  aria-label={labels.actionsLabel}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {rowActions.map((a) => (
                  <React.Fragment key={a.action}>
                    {a.separator ? <DropdownMenuSeparator /> : null}
                    {a.href ? (
                      <DropdownMenuItem
                        asChild
                        className={a.destructive ? 'text-destructive focus:text-destructive' : ''}
                      >
                        <Link to={a.href} data-testid={`reservation-action-${a.action}-${item.id}`}>
                          {a.label}
                        </Link>
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        data-testid={`reservation-action-${a.action}-${item.id}`}
                        className={a.destructive ? 'text-destructive focus:text-destructive' : ''}
                        onClick={() => {
                          if (a.action === 'block_availability') {
                            onBlockAvailability?.(item.mediaUnitId);
                          } else {
                            onAction(a.action, item.id);
                          }
                        }}
                      >
                        {a.label}
                      </DropdownMenuItem>
                    )}
                  </React.Fragment>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </TableCell>
      </TableRow>

      {/* Expanded waiting line — the SAME component and the same
          colSpan-row treatment the unit detail panel uses
          (UnitReservationsTab, CU-86cb3x6y6). */}
      {hasQueue && isQueueOpen && queueLabels && onQueueAction ? (
        <TableRow
          data-testid={`reservation-queue-row-${item.id}`}
          className="border-b border-border/60 bg-muted/10 hover:bg-muted/10"
        >
          <TableCell colSpan={10} className="px-4 py-2">
            <ReservationQueueList
              reservationId={item.id}
              entries={queueEntries}
              currentUserId={currentUserId}
              permissions={{
                canManageQueue: permissions.canManageWaitlist ?? false,
                // CU-86cb3x6y6: displace-and-promote requires BOTH
                // manage_waitlist AND force_release.
                canDisplacePromote:
                  (permissions.canManageWaitlist ?? false) && permissions.canForceRelease,
              }}
              labels={queueLabels}
              onQueueAction={(action, entryId) => onQueueAction(action, entryId, item.id)}
            />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
